import { isOffsetDateTime, isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

export type CoreAccessDeviceInventoryEnvironment = 'dev' | 'prod';
export type CoreAccessDeviceInventorySourceState = 'OBSERVED' | 'SOURCE_MISSING' | 'SOURCE_DELETED';
export type CoreAccessDeviceInventoryItemState =
  | 'OBSERVED'
  | 'NO_MAPPING'
  | 'SOURCE_MISSING'
  | 'SOURCE_MISMATCH'
  | 'SOURCE_DELETED';

export interface CoreAccessDeviceInventoryDevice {
  source_doorlock_id: number | null;
  device_id: string;
  gateway_id: string;
  device_type: string | null;
  is_active: boolean;
  provider_support: 'UNVERIFIED';
  core_device_id: string | null;
  revision: number | null;
  authority: 'CORE' | null;
}

export interface CoreAccessDeviceInventorySlot {
  slot_id: string;
  room_id: string;
  source_slot_id: number;
  source_room_id: number;
  source_room_type_id: number;
  source_room_no: number;
  source_slot_no: string;
  slot_version: number;
  room_version: number;
  topology_revision: number;
  slot_observation_revision: number;
  room_observation_revision: number;
  slot_deleted_marker: string | null;
  room_deleted_marker: string | null;
}

export interface CoreAccessDeviceInventoryItem {
  slot_id: string;
  room_id: string;
  state: CoreAccessDeviceInventoryItemState;
  device: CoreAccessDeviceInventoryDevice | null;
}

export interface CoreAccessDeviceInventoryPage {
  authority: 'CORE';
  property_id: string;
  scope: 'LEGACY_LORA';
  source_environment: CoreAccessDeviceInventoryEnvironment;
  source_branch_id: number;
  observed_at: string;
  source_state: CoreAccessDeviceInventorySourceState;
  core_slots: CoreAccessDeviceInventorySlot[];
  items: CoreAccessDeviceInventoryItem[];
  next_cursor: string | null;
}

export interface CoreAccessDeviceInventoryFilter {
  roomId?: string;
  slotId?: string;
  cursor?: string;
  limit?: number;
}

const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const exactKeys = (value: Record<string, unknown>, keys: readonly string[]) =>
  Object.keys(value).length === keys.length && keys.every((key) => key in value);
const safeInteger = (value: unknown): value is number => Number.isSafeInteger(value);
const nullableText = (value: unknown): value is string | null =>
  value === null || typeof value === 'string';
const sourceStates: readonly CoreAccessDeviceInventorySourceState[] = [
  'OBSERVED',
  'SOURCE_MISSING',
  'SOURCE_DELETED',
];
const itemStates: readonly CoreAccessDeviceInventoryItemState[] = [
  'OBSERVED',
  'NO_MAPPING',
  'SOURCE_MISSING',
  'SOURCE_MISMATCH',
  'SOURCE_DELETED',
];

function invalid(): ApiError {
  return new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '장비 연결 정보를 불러오지 못했습니다. 관리자에게 문의해 주세요.',
  });
}

function isCoreSlot(value: unknown): value is CoreAccessDeviceInventorySlot {
  return (
    record(value) &&
    exactKeys(value, [
      'slot_id',
      'room_id',
      'source_slot_id',
      'source_room_id',
      'source_room_type_id',
      'source_room_no',
      'source_slot_no',
      'slot_version',
      'room_version',
      'topology_revision',
      'slot_observation_revision',
      'room_observation_revision',
      'slot_deleted_marker',
      'room_deleted_marker',
    ]) &&
    isUuid(value.slot_id) &&
    isUuid(value.room_id) &&
    [
      value.source_slot_id,
      value.source_room_id,
      value.source_room_type_id,
      value.source_room_no,
      value.slot_version,
      value.room_version,
      value.topology_revision,
      value.slot_observation_revision,
      value.room_observation_revision,
    ].every(safeInteger) &&
    typeof value.source_slot_no === 'string' &&
    nullableText(value.slot_deleted_marker) &&
    nullableText(value.room_deleted_marker)
  );
}

function isDevice(value: unknown): value is CoreAccessDeviceInventoryDevice {
  return (
    record(value) &&
    exactKeys(value, [
      'source_doorlock_id',
      'device_id',
      'gateway_id',
      'device_type',
      'is_active',
      'provider_support',
      'core_device_id',
      'revision',
      'authority',
    ]) &&
    (value.source_doorlock_id === null || safeInteger(value.source_doorlock_id)) &&
    typeof value.device_id === 'string' &&
    typeof value.gateway_id === 'string' &&
    nullableText(value.device_type) &&
    typeof value.is_active === 'boolean' &&
    value.provider_support === 'UNVERIFIED' &&
    (value.core_device_id === null || isUuid(value.core_device_id)) &&
    (value.revision === null || safeInteger(value.revision)) &&
    (value.authority === null || value.authority === 'CORE')
  );
}

function isItem(value: unknown): value is CoreAccessDeviceInventoryItem {
  if (
    !record(value) ||
    !exactKeys(value, ['slot_id', 'room_id', 'state', 'device']) ||
    !isUuid(value.slot_id) ||
    !isUuid(value.room_id) ||
    !itemStates.includes(value.state as CoreAccessDeviceInventoryItemState)
  )
    return false;
  if (value.state === 'NO_MAPPING' || value.state === 'SOURCE_MISSING')
    return value.device === null;
  return isDevice(value.device);
}

function inventoryPage(
  value: unknown,
  propertyId: string,
  environment: CoreAccessDeviceInventoryEnvironment,
  limit: number,
  filter: CoreAccessDeviceInventoryFilter,
): CoreAccessDeviceInventoryPage {
  if (
    !record(value) ||
    !exactKeys(value, [
      'authority',
      'property_id',
      'scope',
      'source_environment',
      'source_branch_id',
      'observed_at',
      'source_state',
      'core_slots',
      'items',
      'next_cursor',
    ]) ||
    value.authority !== 'CORE' ||
    !isUuid(value.property_id) ||
    value.property_id.toLowerCase() !== propertyId.toLowerCase() ||
    value.scope !== 'LEGACY_LORA' ||
    value.source_environment !== environment ||
    !safeInteger(value.source_branch_id) ||
    !isOffsetDateTime(value.observed_at) ||
    !sourceStates.includes(value.source_state as CoreAccessDeviceInventorySourceState) ||
    !Array.isArray(value.core_slots) ||
    value.core_slots.length > limit ||
    !value.core_slots.every(isCoreSlot) ||
    !Array.isArray(value.items) ||
    value.items.length !== value.core_slots.length ||
    !value.items.every(isItem) ||
    !(value.next_cursor === null || isUuid(value.next_cursor))
  )
    throw invalid();

  const slots = value.core_slots;
  const items = value.items;
  const slotIds = slots.map((slot) => slot.slot_id.toLowerCase());
  if (
    new Set(slotIds).size !== slotIds.length ||
    slotIds.some((slotId, index) => {
      if (index === 0) return false;
      const previous = slotIds[index - 1];
      return previous === undefined || previous >= slotId;
    }) ||
    (filter.cursor !== undefined &&
      slotIds.length > 0 &&
      (slotIds[0] ?? '') <= filter.cursor.toLowerCase()) ||
    slots.some((slot, index) => {
      const item = items[index];
      if (!item) return true;
      return (
        item.slot_id.toLowerCase() !== slot.slot_id.toLowerCase() ||
        item.room_id.toLowerCase() !== slot.room_id.toLowerCase() ||
        (filter.roomId !== undefined &&
          (slot.room_id.toLowerCase() !== filter.roomId.toLowerCase() ||
            item.room_id.toLowerCase() !== filter.roomId.toLowerCase())) ||
        (filter.slotId !== undefined &&
          (slot.slot_id.toLowerCase() !== filter.slotId.toLowerCase() ||
            item.slot_id.toLowerCase() !== filter.slotId.toLowerCase()))
      );
    }) ||
    (value.next_cursor !== null &&
      (slotIds.length === 0 || value.next_cursor.toLowerCase() !== slotIds.at(-1)))
  )
    throw invalid();

  return value as unknown as CoreAccessDeviceInventoryPage;
}

/** Read the independent Core property DTO without changing the active Legacy adapter. */
export async function readCoreAccessDeviceInventory(
  client: ServiceClient,
  propertyId: string,
  filter: CoreAccessDeviceInventoryFilter | undefined,
  environment: CoreAccessDeviceInventoryEnvironment,
  signal?: AbortSignal,
): Promise<CoreAccessDeviceInventoryPage> {
  filter ??= {};
  if (
    !isUuid(propertyId) ||
    (filter.roomId !== undefined && !isUuid(filter.roomId)) ||
    (filter.slotId !== undefined && !isUuid(filter.slotId)) ||
    (filter.cursor !== undefined && !isUuid(filter.cursor)) ||
    (filter.limit !== undefined &&
      (!Number.isInteger(filter.limit) || filter.limit < 1 || filter.limit > 50))
  )
    throw invalid();

  const data = await client.get<unknown>(`admin/properties/${propertyId}/access-devices`, {
    params: {
      room_id: filter.roomId,
      slot_id: filter.slotId,
      cursor: filter.cursor,
      limit: filter.limit,
    },
    signal,
  });
  return inventoryPage(data, propertyId, environment, filter.limit ?? 50, filter);
}
